import { DateTime } from 'luxon';
import { environment } from './environment';

// Define log levels
enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  HTTP = 3,
  DEBUG = 4,
}

// Map log levels to names
const logLevelNames: Record<LogLevel, string> = {
  [LogLevel.ERROR]: 'ERROR',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.HTTP]: 'HTTP',
  [LogLevel.DEBUG]: 'DEBUG',
};

type ConsoleMethod = 'error' | 'warn' | 'info' | 'log' | 'debug';
const logMethods: Record<LogLevel, ConsoleMethod> = {
  [LogLevel.ERROR]: 'error',
  [LogLevel.WARN]: 'warn',
  [LogLevel.INFO]: 'info',
  [LogLevel.HTTP]: 'log',
  [LogLevel.DEBUG]: 'debug',
};

// Determine current log level based on environment
const currentLogLevel = environment.isDevelopment() ? LogLevel.DEBUG : LogLevel.INFO;

/**
 * Format a log message with timestamp
 */
const formatLogMessage = (level: string, message: string): string => {
  const timestamp = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss.SSS');
  return `${timestamp} ${level}: ${message}`;
};

/**
 * Log a message if its level is less than or equal to the current log level
 */
const log = (level: LogLevel, message: string, ...meta: unknown[]): void => {
  if (level <= currentLogLevel) {
    const levelName = logLevelNames[level];
    const formattedMessage = formatLogMessage(levelName, message);

    // Use the appropriate console method
    const method = logMethods[level];

    if (meta.length > 0) {
      console[method](formattedMessage, ...meta);
    } else {
      console[method](formattedMessage);
    }
  }
};

// Export logger functions
export const logInfo = (message: string, ...meta: unknown[]): void => {
  log(LogLevel.INFO, message, ...meta);
};

export const logError = (message: string, ...meta: unknown[]): void => {
  log(LogLevel.ERROR, message, ...meta);
};

export const logWarn = (message: string, ...meta: unknown[]): void => {
  log(LogLevel.WARN, message, ...meta);
};

export const logDebug = (message: string, ...meta: unknown[]): void => {
  log(LogLevel.DEBUG, message, ...meta);
};

export const logHttp = (message: string, ...meta: unknown[]): void => {
  log(LogLevel.HTTP, message, ...meta);
};
